/** @shell standard @header bar */
import React, { useState, useMemo, useEffect, memo } from 'react';
import { View, Text, StyleSheet, Alert, RefreshControl, Platform } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withSpring, withTiming, withDelay } from 'react-native-reanimated';
import { useNavigation } from '@react-navigation/native';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useOrders } from '../data/domains/orders';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useRequireOnlineOrdersEnabled } from '../shared/navigation/useFeatureGateExit';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { useTranslation, getApiText } from '../shared/i18n';
import { useRequireVerified } from '../shared/auth/useRequireVerified';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import { hexToRgba } from '../shared/theme/colorUtils';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  androidScaleFont,
} from '../shared/theme/studio/stackScreenShell';
import type { Order, OrderItem } from '../data/domains/orders';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { formatOrderDate } from '../shared/utils/dateUtils';
import { formatCurrency, getDefaultCurrencyCode } from '../shared/utils/currencyUtils';
import { HorizontalLtrScrollView } from '../components/ui/HorizontalLtrScrollView';

const ENTER_SPRING = { stiffness: 180, damping: 22 };

function getStatusColor(status: string, c: ThemeColors): string {
  switch (status?.toLowerCase()) {
    case 'delivered':
      return c.success;
    case 'pending':
      return c.warning;
    case 'cancelled':
    case 'failed':
      return c.error;
    case 'processing':
      return c.alert;
    default:
      return c.textSecondary;
  }
}

function getDisplayStatus(order: Order): string {
  const payment = (order.payment_status ?? '').toLowerCase();
  if (payment === 'failed') {return 'failed';}
  return (order.status ?? 'pending').toLowerCase();
}

interface OrderCardProps {
  order: Order;
  onPress: () => void;
  onReorder: () => void;
  styles: ReturnType<typeof createOrderHistoryStyles>;
  themeColors: ThemeColors;
  index: number;
}

const OrderCard: React.FC<OrderCardProps> = memo(({ order, onPress, onReorder, styles, themeColors, index }) => {
  const { t } = useTranslation();
  const enterOpacity = useSharedValue(0);
  const enterTranslateY = useSharedValue(20);

  useEffect(() => {
    enterOpacity.value = withTiming(1, { duration: 320 });
    enterTranslateY.value = withDelay(index * 55, withSpring(0, ENTER_SPRING));
  }, [index, enterOpacity, enterTranslateY]);

  const cardEnterStyle = useAnimatedStyle(() => ({
    opacity: enterOpacity.value,
    transform: [{ translateY: enterTranslateY.value }],
  }));

  const status = getDisplayStatus(order);
  const status_color = getStatusColor(status, themeColors);
  const currency = order.currency ?? getDefaultCurrencyCode();
  const format_price = (amount: number) => formatCurrency(amount, { currency });
  const items = order.items ?? [];
  const subtotal = items.reduce(
    (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1),
    0,
  );
  const delivery_fee = order.delivery_fee ?? 0;
  const discount = order.discount ?? 0;
  const total = typeof order.total === 'number' ? order.total : Number(order.total) || 0;
  const coupon_code = order.coupon_code?.trim?.();
  const redeem_from_loyalty = Number(order.redeem_from_loyalty ?? 0);

  return (
    <Animated.View style={[styles.orderCardWrap, cardEnterStyle]}>
      <View style={styles.orderCard}>
        <Clickable
          onPress={onPress}
          accessibilityLabel={`${t('order.orderNumber')} ${order.id}`}
          accessibilityRole="button"
        >
          <View style={styles.cardBody}>
            <View style={styles.orderHeader}>
              <View style={styles.orderHeaderLeft}>
                <Text style={styles.orderOverline}>{t('order.orderNumber').replace(/#/g, '').trim()}</Text>
                <Text style={styles.orderNumber}>#{order.id}</Text>
                <Text style={styles.orderDate}>{formatOrderDate(order.date || order.created_at)}</Text>
              </View>
              <View style={[styles.statusBadge, { backgroundColor: status_color + '20' }]}>
                <View style={[styles.statusDot, { backgroundColor: status_color }]} />
                <Text style={[styles.statusText, { color: status_color }]}>{status}</Text>
              </View>
            </View>

            <View style={styles.itemsTable}>
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.tableHeaderCell, styles.tableColItem]}>{t('order.itemFallback')}</Text>
                <Text style={[styles.tableHeaderCell, styles.tableColQty]}>{t('order.qty')}</Text>
                <Text style={[styles.tableHeaderCell, styles.tableColPrice]}>{t('order.price')}</Text>
              </View>
              {items.map((item: OrderItem, index: number) => {
                const name = getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name || item.title || '—';
                const qty = item.quantity ?? 1;
                const price = item.price ?? 0;
                const line_total = price * qty;
                return (
                  <View key={item.id ?? index} style={[styles.tableRow, index > 0 && styles.tableRowBorder]}>
                    <Text style={[styles.tableCell, styles.tableColItem]} numberOfLines={1}>{name}</Text>
                    <Text style={[styles.tableCell, styles.tableColQty]}>{qty}</Text>
                    <Text style={[styles.tableCell, styles.tableColPrice]}>{format_price(line_total)}</Text>
                  </View>
                );
              })}
            </View>

            <View style={styles.summaryBlock}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>{t('order.subtotal')}</Text>
                <Text style={styles.summaryValue}>{format_price(subtotal)}</Text>
              </View>
              {delivery_fee > 0 && (
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>{t('order.deliveryFee')}</Text>
                  <Text style={styles.summaryValue}>{format_price(delivery_fee)}</Text>
                </View>
              )}
              {discount > 0 && (
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>{t('order.discount')}</Text>
                  <Text style={[styles.summaryValue, styles.discountValue]}>-{format_price(discount)}</Text>
                </View>
              )}
              {coupon_code ? (
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>{t('checkout.couponCode')}</Text>
                  <Text style={styles.summaryValue}>{coupon_code}</Text>
                </View>
              ) : null}
              {redeem_from_loyalty > 0 && (
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>{t('order.loyaltyRedeem')}</Text>
                  <Text style={[styles.summaryValue, styles.discountValue]}>-{format_price(redeem_from_loyalty)}</Text>
                </View>
              )}
              <View style={styles.summaryDivider} />
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>{t('order.total')}</Text>
                <Text style={styles.totalAmount}>{format_price(total)}</Text>
              </View>
            </View>
          </View>
        </Clickable>

        <Clickable
          style={styles.reorderWrap}
          onPress={onReorder}
          accessibilityLabel={t('order.reorder')}
          accessibilityRole="button"
        >
          <View style={styles.reorderButtonInner}>
            <SvgIcon name="reload" size={20} color={themeColors.primary} />
            <Text style={styles.reorderButtonText}>{t('order.reorder')}</Text>
          </View>
        </Clickable>
      </View>
    </Animated.View>
  );
});

function createOrderHistoryStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  return StyleSheet.create({
    contentWrap: { flex: 1 },
    filterRow: {
      flexDirection: 'row',
      gap: appTheme.spacing.sm,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      marginBottom: STACK_SECTION_GAP,
    },
    filterPill: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: hexToRgba(c.text, 0.04),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.border, 0.35),
    },
    filterPillActive: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    filterText: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(13),
      color: c.textSecondary,
      fontWeight: '600',
    },
    filterTextActive: {
      color: c.onPrimary,
    },
    filtersScroll: {
      flexDirection: 'row',
      gap: appTheme.spacing.sm,
      paddingHorizontal: STACK_SCROLL_GUTTER,
    },
    scroll: { flex: 1 },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingBottom: appTheme.spacing.xxl,
      gap: STACK_SECTION_GAP,
    },
    orderCardWrap: {},
    orderCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.card.padding.lg,
      overflow: 'hidden',
    },
    cardBody: {},
    orderHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: appTheme.spacing.lg,
    },
    orderHeaderLeft: { flex: 1 },
    orderOverline: {
      ...appTheme.typography.captionSmall,
      color: c.textLight,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
      marginBottom: 2,
    },
    orderNumber: {
      ...appTheme.typography.h3,
      color: c.text,
      fontWeight: '700',
      letterSpacing: 0.3,
      marginBottom: 4,
    },
    orderDate: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
    },
    statusBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: 6,
      borderRadius: appTheme.borderRadius.pill,
    },
    statusDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
    },
    statusText: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '600',
      textTransform: 'capitalize',
    },
    itemsTable: {
      marginBottom: appTheme.spacing.lg,
      backgroundColor: c.surfaceLight ?? c.background,
      borderRadius: appTheme.borderRadius.md,
      overflow: 'hidden',
      borderWidth: 0,
    },
    tableHeaderRow: {
      flexDirection: 'row',
      backgroundColor: c.surfaceLight ?? c.background,
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: c.borderLight,
    },
    tableHeaderCell: {
      ...appTheme.typography.bodyMedium,
      color: c.textSecondary,
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    tableRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.md,
    },
    tableRowBorder: {
      borderTopWidth: 1,
      borderTopColor: c.borderLight,
    },
    tableCell: {
      ...appTheme.typography.bodySmall,
      color: c.text,
    },
    tableColItem: { flex: 1, marginRight: appTheme.spacing.sm },
    tableColQty: { width: 40, textAlign: 'center' },
    tableColPrice: { width: 76, textAlign: 'right', fontWeight: '600' },
    summaryBlock: {
      marginBottom: appTheme.spacing.lg,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: appTheme.spacing.sm,
    },
    summaryLabel: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    summaryValue: {
      ...appTheme.typography.bodySmall,
      color: c.text,
    },
    discountValue: {
      color: c.success,
      fontWeight: '600',
    },
    summaryDivider: {
      height: 1,
      backgroundColor: c.borderLight,
      marginVertical: appTheme.spacing.md,
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: c.surfaceLight ?? c.background,
      marginHorizontal: -appTheme.card.padding.lg,
      paddingHorizontal: appTheme.card.padding.lg,
      paddingVertical: appTheme.spacing.lg,
      marginTop: appTheme.spacing.xs,
    },
    totalLabel: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '700',
    },
    totalAmount: {
      ...appTheme.typography.h3,
      color: c.primary,
      fontWeight: '700',
    },
    reorderWrap: {
      marginTop: appTheme.spacing.lg,
    },
    reorderButtonInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.xl,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: hexToRgba(c.primary, 0.08),
      minHeight: appTheme.sizes.buttonHeightLarge,
      gap: appTheme.spacing.sm,
    },
    reorderButtonText: {
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.primary,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xxl * 2,
      paddingHorizontal: appTheme.spacing.xl,
    },
    emptyTitle: {
      ...appTheme.typography.h3,
      color: c.text,
      marginTop: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
    },
    emptyText: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      textAlign: 'center',
    },
  });
}

export const OrderHistoryScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const go_back = useExitOrGoBack();
  const show_online_orders = useRequireOnlineOrdersEnabled();
  const { t } = useTranslation();
  useRequireVerified('OrderHistory');
  const [filter, setFilter] = useState('all');
  const { data: orders = [], isLoading, error, refetch } = useOrders();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createOrderHistoryStyles(themeColors), [themeColors]);
  const { scrollY, onScroll } = useScreenHeaderScroll();

  const filters = [
    { id: 'all', label: t('order.all') },
    { id: 'delivered', label: t('order.delivered') },
    { id: 'pending', label: t('order.pending') },
    { id: 'failed', label: t('order.failed') },
    { id: 'cancelled', label: t('order.cancelled') },
  ];

  const filteredOrders = filter === 'all' ? orders : orders.filter(o => getDisplayStatus(o) === filter);
  const insets = useSafeAreaInsets();

  if (!show_online_orders) {
    return null;
  }

  if (isLoading) {
    return (
      <StackScreenShell colors={themeColors}>
        <ScreenHeader title={t('order.title')} onBack={go_back} scrollY={scrollY} />
        <LoadingState message={t('order.loadingOrders')} />
      </StackScreenShell>
    );
  }

  if (error) {
    return (
      <StackScreenShell colors={themeColors}>
        <ScreenHeader title={t('order.title')} onBack={go_back} scrollY={scrollY} />
        <ErrorState
          title={t('order.failedToLoadOrders')}
          message={t('order.tryAgain')}
          onRetry={refetch}
          error={error}
          issue_context={{
            page: t('account.orderHistory'),
            issue: 'Failed to load order history',
            kind: 'api',
            api_endpoint: 'orders',
            http_method: 'GET',
          }}
        />
      </StackScreenShell>
    );
  }

  const content_top = androidAppHeaderContentTop(insets.top, appTheme.spacing.sm);

  return (
    <StackScreenShell colors={themeColors}>
      <ScreenHeader title={t('order.title')} onBack={go_back} scrollY={scrollY} />
      <View style={styles.contentWrap}>
        <View style={{ paddingTop: content_top, paddingBottom: appTheme.spacing.sm }}>
          <HorizontalLtrScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filtersScroll}>
            {filters.map(f => (
              <Clickable key={f.id} onPress={() => setFilter(f.id)} accessibilityLabel={f.label} accessibilityRole="button">
                <View style={[styles.filterPill, filter === f.id && styles.filterPillActive]}>
                  <Text style={[styles.filterText, filter === f.id && styles.filterTextActive]}>{f.label}</Text>
                </View>
              </Clickable>
            ))}
          </HorizontalLtrScrollView>
        </View>

        <Animated.ScrollView
          style={styles.scroll}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + appTheme.spacing.xxl }]}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <RefreshControl
              refreshing={false}
              onRefresh={() => void refetch()}
              tintColor={themeColors.primary}
              colors={Platform.OS === 'android' ? [themeColors.primary] : undefined}
              progressBackgroundColor={Platform.OS === 'android' ? themeColors.surface : undefined}
            />
          }>
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order, index) => (
              <OrderCard
                key={String(order.id)}
                index={index}
                order={order}
                styles={styles}
                themeColors={themeColors}
                onPress={() => navigation.navigate('OrderDetails', { order })}
                onReorder={() => {
                  Alert.alert(t('order.reorderConfirmTitle'), t('order.reorderConfirmMessage'), [
                    { text: t('order.cancel'), style: 'cancel' },
                    {
                      text: t('order.reorder'),
                      onPress: () => navigation.navigate('Checkout', { plan: order, isReorder: true }),
                    },
                  ]);
                }}
              />
            ))
          ) : (
            <View style={[styles.emptyState, studioCardShell(themeColors), { borderRadius: DASH_STUDIO_RADIUS }]}>
              <SvgIcon name="receipt" size={64} color={themeColors.textLight} />
              <Text style={styles.emptyTitle}>{t('order.noOrders')}</Text>
              <Text style={styles.emptyText}>{t('order.noOrdersMessage')}</Text>
            </View>
          )}
        </Animated.ScrollView>
      </View>
    </StackScreenShell>
  );
};

export default OrderHistoryScreen;
